import { Transaction } from '@/types/transaction';

export interface PotentialTransferMatch {
  matchingTransaction: Transaction;
  confidence: number; // 0-100 percentage
  matchCriteria: {
    amountMatch: boolean;
    exactAmountMatch: boolean; // NEW: for exact matches
    dateMatch: boolean;
    timeMatch: boolean;
    accountsDifferent: boolean;
    typesOpposite: boolean;
    currencyMatch: boolean; // NEW: track if currencies are compatible
  };
}

/**
 * Find potential transfer matches for a given transaction
 * Looks for transactions with matching amount and exact same date in the specified target account
 * Enhanced with currency-aware amount matching
 */
export function findPotentialTransferMatches(
  targetTransaction: Transaction,
  allTransactions: Transaction[],
  targetAccountId?: string, // REQUIRED: Only search within this specific account
  accounts?: Array<{id: string, currency: string}>, // NEW: Account data for currency checking
  options: {
    amountTolerancePercentage?: number;
    exchangeRate?: number; // NEW: Exchange rate for currency conversion
  } = {}
): PotentialTransferMatch[] {
  const {
    amountTolerancePercentage = 1.0, // Allow 1% difference for different currency matching
    exchangeRate = 1.0 // Default to 1.0 if not provided
  } = options;

  const matches: PotentialTransferMatch[] = [];
  const targetDate = new Date(targetTransaction.date);
  const sourceAmount = Math.abs(targetTransaction.amount);

  console.log(`🔍 Transfer detection: ${sourceAmount} in account ${targetAccountId} (${allTransactions.length} transactions)`);

  // Only look for matches if this is an income or expense transaction
  if (targetTransaction.type === 'transfer') {
    console.log('❌ Target is already a transfer, skipping detection');
    return matches;
  }

  // STRICT REQUIREMENT: targetAccountId must be provided for precise matching
  if (!targetAccountId) {
    console.log('❌ No target account specified - transfer matching requires specific receiver account');
    return matches;
  }

  // Get account currency information
  const sourceAccount = accounts?.find(a => a.id === targetTransaction.account_id);
  const targetAccount = accounts?.find(a => a.id === targetAccountId);
  const sourceCurrency = sourceAccount?.currency;
  const targetCurrency = targetAccount?.currency;
  const isSameCurrency = sourceCurrency && targetCurrency && sourceCurrency === targetCurrency;

  // Calculate the expected amount in target currency
  let expectedTargetAmount: number;
  if (isSameCurrency) {
    expectedTargetAmount = sourceAmount; // Same currency, no conversion
  } else {
    expectedTargetAmount = sourceAmount * exchangeRate; // Convert using exchange rate
  }

  console.log(`💱 Currency info: Source=${sourceCurrency} (${sourceAmount}), Target=${targetCurrency} (expected: ${expectedTargetAmount.toFixed(2)}), Same=${isSameCurrency}, Rate=${exchangeRate}`);

  let candidatesChecked = 0;
  let candidatesSkipped = 0;

  // Log transactions in target account for debugging
  const transactionsInTargetAccount = allTransactions.filter(t => t.account_id === targetAccountId);
  console.log(`📊 ${transactionsInTargetAccount.length} transactions in target account`);
  
  // Debug: Show sample transactions in target account
  if (transactionsInTargetAccount.length > 0) {
    console.log(`📝 Sample transactions in target account:`, transactionsInTargetAccount.slice(0, 3).map(t => ({
      id: t.id,
      type: t.type,
      amount: t.amount,
      date: t.date,
      description: t.description
    })));
  }

  for (const transaction of allTransactions) {
    // Skip the same transaction
    if (transaction.id === targetTransaction.id) {
      candidatesSkipped++;
      continue;
    }
    
    // Skip if already a transfer
    if (transaction.type === 'transfer') {
      candidatesSkipped++;
      continue;
    }
    
    // Skip if same account as source
    if (transaction.account_id === targetTransaction.account_id) {
      candidatesSkipped++;
      continue;
    }

    // STRICT: Only search within the specified target account
    if (transaction.account_id !== targetAccountId) {
      candidatesSkipped++;
      continue;
    }

    candidatesChecked++;
    const transactionDate = new Date(transaction.date);
    const transactionAmount = Math.abs(transaction.amount);

    console.log(`🔍 Checking: ${transaction.type} ${transactionAmount} on ${transactionDate.toDateString()}`);

    // Enhanced amount matching logic based on currency
    const amountDifference = Math.abs(expectedTargetAmount - transactionAmount);
    let amountMatch = false;
    
    if (isSameCurrency) {
      // For same currency accounts: EXACT amount match required (zero tolerance)
      amountMatch = amountDifference === 0;
      console.log(`  💰 Same currency - exact match required: ${amountDifference === 0 ? 'PASS' : 'FAIL'} (diff: ${amountDifference.toFixed(2)})`);
    } else {
      // For different currency accounts: Use tolerance on the converted amount
      const amountToleranceValue = expectedTargetAmount * (amountTolerancePercentage / 100);
      amountMatch = amountDifference <= amountToleranceValue;
      console.log(`  💰 Different currency - tolerance allowed: ${amountMatch ? 'PASS' : 'FAIL'} (expected: ${expectedTargetAmount.toFixed(2)}, actual: ${transactionAmount}, diff: ${amountDifference.toFixed(2)}, tolerance: ${amountToleranceValue.toFixed(2)})`);
    }

    if (!amountMatch) {
      console.log(`  ❌ Amount match failed`);
      continue;
    }

    // Check if it's the exact same day (strict matching)
    const sameDay = 
      targetDate.getFullYear() === transactionDate.getFullYear() &&
      targetDate.getMonth() === transactionDate.getMonth() &&
      targetDate.getDate() === transactionDate.getDate();

    if (!sameDay) {
      console.log(`  ❌ Not same day`);
      continue;
    }

    // Check if transaction types are opposite (for merging existing transfers)
    // OR if we're looking for a complementary transaction in the target account
    const typesOpposite = 
      (targetTransaction.type === 'income' && transaction.type === 'expense') ||
      (targetTransaction.type === 'expense' && transaction.type === 'income');
    
    // For transfer creation: if the source is expense, look for income in target account
    // If source is income, look for expense in target account  
    const typesComplementary = typesOpposite;

    const dateMatch = sameDay; // Same day matching
    const accountsDifferent = transaction.account_id !== targetTransaction.account_id;
    const timeMatch = sameDay; // For compatibility, same as date match
    const currencyMatch = isSameCurrency !== undefined; // We have currency info

    // Calculate confidence score
    let confidence = 0;
    let scoreBreakdown = [];
    
    // Check for exact amount match (after conversion)
    const exactAmountMatch = amountDifference === 0;
    
    // Amount confidence based on currency and difference
    if (amountMatch) {
      if (exactAmountMatch) {
        // Perfect match gets full amount points
        confidence += 50;
        scoreBreakdown.push('Exact amount: +50');
      } else if (!isSameCurrency) {
        // Only give partial credit for different currencies with tolerance
        const amountDifferencePercentage = (amountDifference / expectedTargetAmount) * 100;
        const amountScore = Math.max(15, 50 - (amountDifferencePercentage * 15)); // Scale down from 50 to 15 minimum
        confidence += Math.round(amountScore);
        scoreBreakdown.push(`Similar amount (${amountDifferencePercentage.toFixed(1)}% diff): +${Math.round(amountScore)}`);
      }
    }
    
    if (dateMatch) {
      confidence += 30;
      scoreBreakdown.push('Same day: +30');
    }
    if (typesComplementary) {
      confidence += 15;
      scoreBreakdown.push('Complementary types: +15');
    }
    if (accountsDifferent) {
      confidence += 5;
      scoreBreakdown.push('Different accounts: +5');
    }

    // Only consider matches with reasonable confidence
    if (confidence >= 70) {
      console.log(`✅ Found match: ${confidence}% confidence`);
      matches.push({
        matchingTransaction: transaction,
        confidence: confidence,
        matchCriteria: {
          amountMatch,
          exactAmountMatch,
          dateMatch,
          timeMatch, // For compatibility
          accountsDifferent,
          typesOpposite: typesComplementary,
          currencyMatch
        }
      });
    }
  }

  console.log(`📈 Summary: Checked ${candidatesChecked} candidates, skipped ${candidatesSkipped}, found ${matches.length} matches`);
  console.log(`🎯 Found ${matches.length} potential transfer matches in target account ${targetAccountId}`);

  // Sort by confidence (highest first)
  return matches.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Find potential transfer matches with relaxed criteria for older transactions
 * Still requires same-day matching and specific target account, but with more lenient confidence scoring
 * Enhanced with currency-aware amount matching
 */
export function findPotentialTransferMatchesRelaxed(
  targetTransaction: Transaction,
  allTransactions: Transaction[],
  targetAccountId?: string, // REQUIRED: Only search within this specific account
  accounts?: Array<{id: string, currency: string}>, // NEW: Account data for currency checking
  options: {
    amountTolerancePercentage?: number;
    minimumConfidence?: number; // Lower confidence threshold
    exchangeRate?: number; // NEW: Exchange rate for currency conversion
  } = {}
): PotentialTransferMatch[] {
  const {
    amountTolerancePercentage = 5.0, // Slightly more lenient for old transactions
    minimumConfidence = 50, // Lower confidence threshold for old transactions
    exchangeRate = 1.0 // Default to 1.0 if not provided
  } = options;

  console.log(`🔍 RELAXED Transfer detection for old transactions:`);
  console.log(`📅 Date tolerance: SAME DAY ONLY (even for relaxed)`);
  console.log(`💰 Amount tolerance: ${amountTolerancePercentage}%`);
  console.log(`📊 Minimum confidence: ${minimumConfidence}%`);

  const matches: PotentialTransferMatch[] = [];
  const targetDate = new Date(targetTransaction.date);
  const sourceAmount = Math.abs(targetTransaction.amount);

  // Only look for matches if this is an income or expense transaction
  if (targetTransaction.type === 'transfer') {
    console.log('❌ Target is already a transfer, skipping detection');
    return matches;
  }

  // STRICT REQUIREMENT: targetAccountId must be provided even for relaxed matching
  if (!targetAccountId) {
    console.log('❌ No target account specified - even relaxed matching requires specific receiver account');
    return matches;
  }

  // Get account currency information
  const sourceAccount = accounts?.find(a => a.id === targetTransaction.account_id);
  const targetAccount = accounts?.find(a => a.id === targetAccountId);
  const sourceCurrency = sourceAccount?.currency;
  const targetCurrency = targetAccount?.currency;
  const isSameCurrency = sourceCurrency && targetCurrency && sourceCurrency === targetCurrency;

  // Calculate the expected amount in target currency
  let expectedTargetAmount: number;
  if (isSameCurrency) {
    expectedTargetAmount = sourceAmount; // Same currency, no conversion
  } else {
    expectedTargetAmount = sourceAmount * exchangeRate; // Convert using exchange rate
  }

  console.log(`💱 RELAXED Currency info: Source=${sourceCurrency} (${sourceAmount}), Target=${targetCurrency} (expected: ${expectedTargetAmount.toFixed(2)}), Same=${isSameCurrency}, Rate=${exchangeRate}`);

  for (const transaction of allTransactions) {
    // Skip the same transaction
    if (transaction.id === targetTransaction.id) continue;
    
    // Skip if already a transfer
    if (transaction.type === 'transfer') continue;
    
    // Skip if same account as source
    if (transaction.account_id === targetTransaction.account_id) continue;

    // STRICT: Only search within the specified target account
    if (transaction.account_id !== targetAccountId) {
      continue;
    }

    const transactionDate = new Date(transaction.date);
    const transactionAmount = Math.abs(transaction.amount);

    // Enhanced amount matching logic based on currency (same as standard detection)
    const amountDifference = Math.abs(expectedTargetAmount - transactionAmount);
    let amountMatch = false;
    
    if (isSameCurrency) {
      // For same currency accounts: EXACT amount match required (zero tolerance even in relaxed mode)
      amountMatch = amountDifference === 0;
      console.log(`  💰 RELAXED Same currency - exact match still required: ${amountDifference === 0 ? 'PASS' : 'FAIL'} (diff: ${amountDifference.toFixed(2)})`);
    } else {
      // For different currency accounts: Use relaxed tolerance on the converted amount
      const amountToleranceValue = expectedTargetAmount * (amountTolerancePercentage / 100);
      amountMatch = amountDifference <= amountToleranceValue;
      console.log(`  💰 RELAXED Different currency - tolerance allowed: ${amountMatch ? 'PASS' : 'FAIL'} (expected: ${expectedTargetAmount.toFixed(2)}, actual: ${transactionAmount}, diff: ${amountDifference.toFixed(2)}, tolerance: ${amountToleranceValue.toFixed(2)})`);
    }

    if (!amountMatch) continue;

    // Check if it's the exact same day (strict matching, even for relaxed)
    const sameDay = 
      targetDate.getFullYear() === transactionDate.getFullYear() &&
      targetDate.getMonth() === transactionDate.getMonth() &&
      targetDate.getDate() === transactionDate.getDate();

    if (!sameDay) continue;

    // Check if transaction types are opposite
    const typesOpposite = 
      (targetTransaction.type === 'income' && transaction.type === 'expense') ||
      (targetTransaction.type === 'expense' && transaction.type === 'income');

    const dateMatch = sameDay;
    const timeMatch = sameDay;
    const accountsDifferent = transaction.account_id !== targetTransaction.account_id;
    const currencyMatch = isSameCurrency !== undefined; // We have currency info

    // Calculate confidence score - ADJUSTED FOR RELAXED MATCHING
    let confidence = 0;
    let scoreBreakdown = [];
    
    // Check for exact amount match (after conversion)
    const exactAmountMatch = amountDifference === 0;
    
    // Amount confidence - more generous for old transactions
    if (amountMatch) {
      if (exactAmountMatch) {
        confidence += 45; // Slightly lower base score for relaxed
        scoreBreakdown.push('Exact amount: +45');
      } else if (!isSameCurrency) {
        // More generous scoring for old different-currency transactions
        const amountDifferencePercentage = (amountDifference / expectedTargetAmount) * 100;
        const amountScore = Math.max(20, 45 - (amountDifferencePercentage * 10)); // More generous scaling
        confidence += Math.round(amountScore);
        scoreBreakdown.push(`Similar amount (${amountDifferencePercentage.toFixed(1)}% diff): +${Math.round(amountScore)}`);
      }
    }
    
    if (dateMatch) {
      confidence += 30; // Same as standard
      scoreBreakdown.push('Same day: +30');
    }
    if (typesOpposite) {
      confidence += 15; // Same as standard
      scoreBreakdown.push('Opposite types: +15');
    }
    if (accountsDifferent) {
      confidence += 10; // Slightly higher for relaxed (was 5)
      scoreBreakdown.push('Different accounts: +10');
    }

    // Use the configurable minimum confidence (default 50% for relaxed)
    if (confidence >= minimumConfidence) {
      console.log(`✅ RELAXED Found match: ${confidence}% confidence (min: ${minimumConfidence}%)`);
      matches.push({
        matchingTransaction: transaction,
        confidence: confidence,
        matchCriteria: {
          amountMatch,
          exactAmountMatch,
          dateMatch,
          timeMatch,
          accountsDifferent,
          typesOpposite,
          currencyMatch
        }
      });
    }
  }

  console.log(`🎯 RELAXED Found ${matches.length} potential transfer matches in target account ${targetAccountId}`);

  // Sort by confidence (highest first)
  return matches.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Create a transfer from two matching transactions
 */
export function createTransferFromMatch(
  sourceTransaction: Transaction,
  targetTransaction: Transaction
): {
  fromTransaction: Transaction;
  toTransaction: Transaction;
  transferData: {
    amount: number;
    fromAccountId: string;
    toAccountId: string;
    date: string;
    description: string;
    notes?: string;
    exchangeRate?: number;
  };
} {
  // Determine which is the source (expense) and which is target (income)
  const isSourceExpense = sourceTransaction.type === 'expense';
  const fromTransaction = isSourceExpense ? sourceTransaction : targetTransaction;
  const toTransaction = isSourceExpense ? targetTransaction : sourceTransaction;

  // Use the absolute amount (should be the same for both)
  const amount = Math.abs(fromTransaction.amount);

  // Determine exchange rate if different currencies
  const fromAmount = Math.abs(fromTransaction.amount);
  const toAmount = Math.abs(toTransaction.amount);
  const exchangeRate = fromAmount > 0 ? toAmount / fromAmount : 1;

  // Keep description empty for transfers as requested
  const description = '';

  // Combine notes
  const notes = [
    fromTransaction.notes,
    toTransaction.notes
  ].filter(note => note && note.trim()).join(' | ') || undefined;

  return {
    fromTransaction,
    toTransaction,
    transferData: {
      amount,
      fromAccountId: fromTransaction.account_id,
      toAccountId: toTransaction.account_id,
      date: fromTransaction.date, // Use the earlier date
      description,
      notes,
      exchangeRate: exchangeRate !== 1 ? exchangeRate : undefined
    }
  };
}

/**
 * Get match criteria translation keys for display
 */
export function getMatchCriteriaKeys(match: PotentialTransferMatch): string[] {
  const criteriaKeys: string[] = [];
  
  // Use exact amount match if available, otherwise similar amount
  if (match.matchCriteria.exactAmountMatch) {
    criteriaKeys.push('transactions.transferMerge.criteria.exactAmount');
  } else if (match.matchCriteria.amountMatch) {
    criteriaKeys.push('transactions.transferMerge.criteria.similarAmount');
  }
  
  if (match.matchCriteria.timeMatch) {
    criteriaKeys.push('transactions.transferMerge.criteria.similarTime');
  }
  
  if (match.matchCriteria.dateMatch) {
    criteriaKeys.push('transactions.transferMerge.criteria.sameDate');
  }
  
  if (match.matchCriteria.typesOpposite) {
    criteriaKeys.push('transactions.transferMerge.criteria.oppositeTypes');
  }
  
  if (match.matchCriteria.accountsDifferent) {
    criteriaKeys.push('transactions.transferMerge.criteria.differentAccounts');
  }
  
  return criteriaKeys;
} 